import { z } from "zod";
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, API_BASE } from "@/lib/http";
import { zSpringPage, zTimestamp } from "./_schemas";

const dashboardSchema = z.object({
  totalPipelines: z.number().default(0),
  activePipelines: z.number().default(0),
  totalConnectors: z.number().default(0),
  totalExecutions: z.number().default(0),
  executionsToday: z.number().default(0),
  successfulToday: z.number().default(0),
  failedToday: z.number().default(0),
  running: z.number().default(0),
});
export type DashboardStats = z.infer<typeof dashboardSchema>;

export const dashboardQuery = queryOptions({
  queryKey: ["monitoring", "dashboard"],
  queryFn: () => apiRequest<unknown>("/monitoring/dashboard").then((r) => dashboardSchema.parse(r)),
});

export function useDashboardStats() {
  return useQuery(dashboardQuery);
}

// ── Anomalies ───────────────────────────────────────────────────────────────

export const anomalySchema = z.object({
  id: z.union([z.string(), z.number()]),
  pipelineId: z.string().nullish(),
  executionId: z.string().nullish(),
  anomalyType: z.string().nullish(),
  severity: z.string().nullish(),
  zScore: z.number().nullish(),
  details: z.string().nullish(),
  detectedAt: zTimestamp.nullish(),
});
export type Anomaly = z.infer<typeof anomalySchema>;

export function useAnomalies(params: { severity?: string; pipelineId?: string } = {}) {
  const search = new URLSearchParams({ size: "100" });
  if (params.severity) search.set("severity", params.severity);
  if (params.pipelineId) search.set("pipelineId", params.pipelineId);
  return useQuery({
    queryKey: ["anomalies", params.severity ?? null, params.pipelineId ?? null],
    queryFn: () =>
      apiRequest<unknown>(`/anomalies?${search}`).then((r) => zSpringPage(anomalySchema).parse(r)),
  });
}

export function useAnomalySummary() {
  return useQuery({
    queryKey: ["anomalies", "summary"],
    queryFn: () =>
      apiRequest<unknown>("/anomalies/summary").then((r) =>
        z.object({ high: z.number(), medium: z.number(), low: z.number() }).partial().parse(r),
      ),
  });
}

export function useDismissAnomaly() {
  return useMutation({
    mutationFn: (id: string | number) => apiRequest<void>(`/anomalies/${id}`, { method: "DELETE" }),
  });
}

// ── Usage ───────────────────────────────────────────────────────────────────

const usageDailySchema = z.object({
  day: z.string(),
  runs: z.number().nullish(),
  successes: z.number().nullish(),
  failures: z.number().nullish(),
  recordsWritten: z.number().nullish(),
  slaBreaches: z.number().nullish(),
});
export const usageSummarySchema = z.object({
  range: z.string().nullish(),
  totalRuns: z.number().default(0),
  totalSuccesses: z.number().default(0),
  totalFailures: z.number().default(0),
  successRate: z.number().default(0),
  totalRecordsWritten: z.number().default(0),
  avgDurationMs: z.number().default(0),
  slaBreaches: z.number().default(0),
  daily: z.array(usageDailySchema).default([]),
  topPipelines: z.record(z.string(), z.number()).default({}),
});
export type UsageSummary = z.infer<typeof usageSummarySchema>;

export function useUsage(range: string) {
  return useQuery({
    queryKey: ["usage", range],
    queryFn: () =>
      apiRequest<unknown>(`/usage?range=${range}`).then((r) => usageSummarySchema.parse(r)),
  });
}

export function usageCsvUrl(range: string): string {
  return `${API_BASE}/usage/export?range=${range}`;
}

// ── Audit log ───────────────────────────────────────────────────────────────

export const auditEntrySchema = z.object({
  id: z.string(),
  actor: z.string().nullish(),
  actorType: z.string().nullish(),
  action: z.string().nullish(),
  resourceType: z.string().nullish(),
  resourceId: z.string().nullish(),
  detail: z.string().nullish(),
  ipAddress: z.string().nullish(),
  occurredAt: zTimestamp.nullish(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export function useAuditLog(params: { range?: string; action?: string; actor?: string } = {}) {
  const search = new URLSearchParams({ size: "100", range: params.range ?? "7d" });
  if (params.action) search.set("action", params.action);
  if (params.actor) search.set("actor", params.actor);
  return useQuery({
    queryKey: ["audit", params.range ?? "7d", params.action ?? null, params.actor ?? null],
    queryFn: () =>
      apiRequest<unknown>(`/audit-log?${search}`).then((r) =>
        zSpringPage(auditEntrySchema).parse(r),
      ),
  });
}

// ── Execution spans / trace viewer ──────────────────────────────────────────

export const spanSchema = z.object({
  spanId: z.string(),
  parentSpanId: z.string().nullish(),
  traceId: z.string().nullish(),
  name: z.string(),
  status: z.string().nullish(),
  startMs: z.number(),
  endMs: z.number(),
  durationMs: z.number(),
  attributes: z.record(z.string(), z.string()).nullish(),
  events: z
    .array(
      z.object({
        name: z.string(),
        offsetMs: z.number(),
        attributes: z.record(z.string(), z.string()).nullish(),
      }),
    )
    .nullish(),
});
export type Span = z.infer<typeof spanSchema>;

export function useExecutionSpans(executionId: string, enabled = true) {
  return useQuery({
    queryKey: ["monitoring", "spans", executionId],
    enabled,
    queryFn: () =>
      apiRequest<unknown>(`/monitoring/executions/${executionId}/spans`).then((r) =>
        z.array(spanSchema).parse(r),
      ),
  });
}
